import { useEffect, useRef } from 'react'
import { generatePath, Redirect, Route, useParams } from 'react-router-dom'
import { StepName } from '@phrasee/phrasee-typings/typings/futurama/content'
import { unifiedFlowPaths } from 'app/navigation/paths'
import CustomSwitch from 'app/router/Switch'
import { useFlags } from 'launchdarkly-react-client-sdk'

import ErrorPage from 'common/components/error/ErrorPage'
import Loader from 'common/components/loaders/Loader'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import useWebsockets from 'common/hooks/useWebsockets'

import useGetContentQuery from '../api/queries/useGetContentQuery'
import {
  resetContentPageState,
  unifiedContentInitialize,
} from '../store/unifiedFlowSlice'

import Header from './components/header/Header'
import useHandleNewLanguageUpdate from './hooks/useHandleNewLanguageUpdate'
import useTrackUnifiedFlowSteps from './hooks/useTrackUnifiedFlowSteps'
import BriefAndGenerateManager from './briefAndGenerateManager'
import { getStepStatus } from './helpers'
import Integrations from './integrations'
import Manage from './manage'
import Results from './results'
import Review from './review'
import useUserTrackingWebsocketSubscription from './useUserTrackingWebsocketSubscription'

const ContentPage = () => {
  const dispatch = useAppDispatch()
  const { shouldUseManageInsteadOfReview, showBothReviewAndManage } = useFlags()

  const getPagePath = (stepName: StepName | undefined) => {
    if (stepName === 'generation') {
      return unifiedFlowPaths.generate
    } else if (stepName === 'launch') {
      return unifiedFlowPaths.launch
    } else if (stepName === 'review') {
      // If showBothReviewAndManage is ON, always show review
      // Otherwise, use existing logic
      return showBothReviewAndManage
        ? unifiedFlowPaths.review
        : shouldUseManageInsteadOfReview
          ? unifiedFlowPaths.manage
          : unifiedFlowPaths.review
    } else if (stepName === 'manage') {
      return unifiedFlowPaths.manage
    } else if (stepName === 'results') {
      return !showBothReviewAndManage && shouldUseManageInsteadOfReview
        ? unifiedFlowPaths.manage
        : unifiedFlowPaths.results
    } else {
      return unifiedFlowPaths.generate
    }
  }

  const { sendJsonMessage, isConnectionReady } = useWebsockets()

  useTrackUnifiedFlowSteps()
  useHandleNewLanguageUpdate()

  const hasRegisteredWebsocketsRef = useRef(false)
  const { contentId } = useParams<{ contentId: string }>()

  useUserTrackingWebsocketSubscription(contentId)

  const activeContentId = useAppSelector(
    (state) => state.unifiedFlow.activeContentId
  )

  const { status, data: content } = useGetContentQuery(contentId)

  const selectedPage = content?.steps_status?.find(
    (step) => step.status === 'active'
  )?.step

  const firstEnabledPage = content?.steps_status?.find(
    (step) => step.status === 'enabled'
  )?.step

  const selectedPageWithFallback = selectedPage || firstEnabledPage
  const selectedPagePath = generatePath(getPagePath(selectedPageWithFallback), {
    contentId,
  })

  const launchStepsStatus = getStepStatus(content?.steps_status, 'launch')
  const resultsStepsStatus = getStepStatus(content?.steps_status, 'results')
  const reviewStepStatus = getStepStatus(content?.steps_status, 'review')
  const manageStepStatus = getStepStatus(content?.steps_status, 'manage')

  useEffect(() => {
    if (content && !activeContentId) {
      dispatch(
        unifiedContentInitialize({
          activeContentId: contentId,
          isUsingUniversalBrief: content.use_universal_brief,
        })
      )
    }
  }, [activeContentId, content, contentId, dispatch])

  useEffect(() => {
    if (content && isConnectionReady && !hasRegisteredWebsocketsRef.current) {
      content.ws_messages?.forEach((message) => {
        sendJsonMessage({
          action: 'subscribe',
          topics: [message.wsTopic],
        })
      })
      hasRegisteredWebsocketsRef.current = true
    }
  }, [content, isConnectionReady, sendJsonMessage])

  useEffect(() => {
    return () => {
      dispatch(resetContentPageState())
    }
  }, [dispatch])

  const getState = () => {
    if (status === 'error') {
      return 'error'
    } else if (!!content && !!activeContentId) {
      return 'success'
    } else {
      return 'loading'
    }
  }

  const state = getState()

  return (
    <>
      {
        {
          loading: <Loader />,
          success: (
            <>
              <Header />
              <CustomSwitch>
                <Route
                  key="unified-flow-content-generate"
                  path={unifiedFlowPaths.generate}
                  exact
                  render={() => <BriefAndGenerateManager />}
                />
                <Route
                  key="unified-flow-content-launch"
                  path={unifiedFlowPaths.launch}
                  exact
                  render={() =>
                    launchStepsStatus === 'disabled' ? (
                      <Redirect to={selectedPagePath} />
                    ) : (
                      <Integrations />
                    )
                  }
                />
                <Route
                  key="unified-flow-content-review"
                  path={unifiedFlowPaths.review}
                  exact
                  render={() => {
                    if (
                      reviewStepStatus === 'disabled' ||
                      reviewStepStatus === 'hidden'
                    ) {
                      return <Redirect to={selectedPagePath} />
                    }
                    // If showBothReviewAndManage is ON, don't redirect
                    if (
                      !showBothReviewAndManage &&
                      shouldUseManageInsteadOfReview &&
                      manageStepStatus !== 'disabled' &&
                      manageStepStatus !== 'hidden'
                    ) {
                      return (
                        <Redirect
                          to={generatePath(unifiedFlowPaths.manage, {
                            contentId,
                          })}
                        />
                      )
                    }
                    return <Review />
                  }}
                />
                <Route
                  key="unified-flow-content-manage"
                  path={unifiedFlowPaths.manage}
                  exact
                  render={() => {
                    // If showBothReviewAndManage is ON, allow access
                    if (
                      (!showBothReviewAndManage &&
                        !shouldUseManageInsteadOfReview) ||
                      manageStepStatus === 'disabled' ||
                      manageStepStatus === 'hidden'
                    ) {
                      return (
                        <Redirect
                          to={generatePath(unifiedFlowPaths.review, {
                            contentId,
                          })}
                        />
                      )
                    }
                    return <Manage />
                  }}
                />
                <Route
                  key="unified-flow-content-results"
                  path={unifiedFlowPaths.results}
                  exact
                  render={() => {
                    if (
                      resultsStepsStatus === 'disabled' ||
                      resultsStepsStatus === 'hidden'
                    ) {
                      return <Redirect to={selectedPagePath} />
                    }
                    if (
                      !showBothReviewAndManage &&
                      shouldUseManageInsteadOfReview
                    ) {
                      return (
                        <Redirect
                          to={generatePath(unifiedFlowPaths.manage, {
                            contentId,
                          })}
                        />
                      )
                    }
                    return <Results />
                  }}
                />
                <Redirect
                  exact
                  from={unifiedFlowPaths.content}
                  to={selectedPagePath}
                />
              </CustomSwitch>
            </>
          ),
          error: <ErrorPage />,
        }[state]
      }
    </>
  )
}

export default ContentPage
